import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ListPage, Badge, useToast } from '@kollez/design-system';
import { usePlatformApprovals, useApproveApproval, useRejectApproval } from '@/lib/queries';

/**
 * Server returns InstitutionApplication + CompanyApplication rows merged.
 * The two shapes carry the org name under different keys, plus a set of
 * kind-specific detail fields. We keep them all optional and pick per-row.
 */
interface ApprovalRow {
  _id: string;
  kind: 'institution' | 'company';
  status: string;
  createdAt?: string;
  submittedAt?: string;
  applicantName?: string;
  applicantEmail?: string;
  applicantPhone?: string;
  applicantRole?: string;
  source?: string;
  verificationDocuments?: Array<{ kind: string; url: string; uploadedAt?: string }>;
  // Institution fields
  institutionName?: string;
  websiteUrl?: string;
  additionalDetails?: string;
  aisheColleges?: unknown[];
  // Company fields
  companyName?: string;
  companyWebsite?: string;
  industry?: string;
  companySize?: string;
  employeeCount?: number;
  foundedYear?: number;
  gstin?: string;
  pan?: string;
  hqCity?: string;
  hqState?: string;
  hiringTargets?: { yearlyHires?: number; campusesTargeted?: number; rolesTypical?: string[] };
}

function orgName(r: ApprovalRow): string {
  return r.kind === 'company'
    ? (r.companyName ?? '(unnamed company)')
    : (r.institutionName ?? '(unnamed institution)');
}

/**
 * /platform/approvals — live queue over InstitutionApplication + CompanyApplication.
 * Real actions:
 *   · Approve → provisions Tenant + admin (institution) or Partner + admin (company)
 *   · Reject → status='rejected' with reason (kept in audit log)
 *   · Request more info → status='in_review' with note (planned server-side)
 */
export function PlatformApprovalsPage() {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const query = usePlatformApprovals(statusFilter);
  const approve = useApproveApproval();
  const reject = useRejectApproval();

  const rows = (query.data?.approvals ?? []) as unknown as ApprovalRow[];

  const kpis = [
    { label: 'Pending', value: rows.filter((r) => r.status === 'pending').length, tone: 'warning' as const },
    { label: 'In review', value: rows.filter((r) => r.status === 'in_review').length, tone: 'info' as const },
    { label: 'Approved · this month', value: rows.filter((r) => r.status === 'approved').length, tone: 'success' as const },
    { label: 'Total shown', value: rows.length },
  ];

  return (
    <ListPage<ApprovalRow>
      eyebrow="Platform ops"
      title="Approvals queue"
      subtitle={query.isLoading ? 'Loading…' : `${rows.length} application${rows.length === 1 ? '' : 's'} · every action is logged`}
      kpis={kpis}
      segment={{
        key: 'kind',
        options: [['all', 'All'], ['institution', 'Institution'], ['company', 'Company']],
      }}
      filters={[
        {
          key: 'status',
          label: 'Status',
          value: statusFilter,
          onChange: setStatusFilter,
          options: [
            ['pending', 'Pending'],
            ['in_review', 'In review'],
            ['approved', 'Approved'],
            ['rejected', 'Rejected'],
          ],
        } as unknown as Parameters<typeof ListPage>[0]['filters'] extends (infer T)[] ? T : never,
      ]}
      search={{ placeholder: 'Search by org name or email', keys: ['institutionName', 'companyName', 'applicantEmail'] as unknown as (keyof ApprovalRow)[] }}
      rows={rows}
      rowKey={(r) => r._id}
      columns={[
        {
          key: 'kind', label: 'Kind',
          render: (r) => <Badge tone={r.kind === 'institution' ? 'info' : 'outline'}>{r.kind}</Badge>,
        },
        {
          key: 'name', label: 'Applicant',
          render: (r) => (
            <div>
              <div className="cell-title">{orgName(r)}</div>
              <div className="cell-sub">
                {r.applicantName ?? '—'}
                {r.applicantRole ? ` · ${r.applicantRole}` : ''}
                {r.applicantEmail ? ` · ${r.applicantEmail}` : ''}
              </div>
            </div>
          ),
        },
        {
          key: 'age', label: 'Age', hideMobile: true,
          render: (r) => humanAge(new Date(r.createdAt ?? r.submittedAt ?? Date.now())),
        },
        {
          key: 'status', label: 'Status', right: true,
          render: (r) => (
            <Badge tone={r.status === 'pending' ? 'warning' : r.status === 'in_review' ? 'info' : r.status === 'approved' ? 'success' : 'outline'}>
              {r.status.replace(/_/g, ' ')}
            </Badge>
          ),
        },
        {
          // Per-row actions. Every button here matches the click-guard
          // inside ListPage (`.closest('input,button,a,select')`), so
          // clicking Approve/Reject/View does NOT also open the drawer.
          key: 'actions', label: '', right: true,
          render: (r) => (
            <div className="flex items-center gap-1 justify-end flex-wrap">
              <Link
                className="btn btn-outline btn-xs"
                to={`/platform/approvals/${r._id}?kind=${r.kind}`}
                aria-label={`View details for ${orgName(r)}`}
              >
                View
              </Link>
              <button
                type="button"
                className="btn btn-primary btn-xs"
                disabled={approve.isPending || r.status === 'approved'}
                aria-label={`Approve ${orgName(r)}`}
                onClick={() => approve.mutate(
                  { id: r._id, kind: r.kind, plan: r.kind === 'institution' ? 'college' : undefined },
                  {
                    onSuccess: () => toast.show({ message: `Provisioned ${orgName(r)}. Admin invite sent to ${r.applicantEmail ?? 'applicant'}.` }),
                    onError: (e: unknown) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Approve failed' }),
                  },
                )}
              >
                {approve.isPending ? '…' : 'Approve'}
              </button>
              <button
                type="button"
                className="btn btn-danger btn-xs"
                disabled={reject.isPending || r.status === 'rejected'}
                aria-label={`Reject ${orgName(r)}`}
                onClick={() => {
                  const reason = window.prompt('Reason for rejection (required, kept in audit log):');
                  if (!reason || reason.trim().length < 5) return;
                  reject.mutate(
                    { id: r._id, kind: r.kind, reason },
                    {
                      onSuccess: () => toast.show({ message: `Rejected ${orgName(r)}. Applicant notified.` }),
                      onError: (e: unknown) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Reject failed' }),
                    },
                  );
                }}
              >
                {reject.isPending ? '…' : 'Reject'}
              </button>
            </div>
          ),
        },
      ]}
      drawer={(r) => ({
        eyebrow: `${r.kind} · ${humanAge(new Date(r.createdAt ?? r.submittedAt ?? Date.now()))} old${r.source ? ` · via ${r.source}` : ''}`,
        title: orgName(r),
        size: 'lg',
        body: (
          <div className="grid gap-4">
            <div>
              <Link
                to={`/platform/approvals/${r._id}?kind=${r.kind}`}
                className="link text-sm"
              >
                Open full detail page →
              </Link>
            </div>
            <div>
              <div className="text-sm font-medium">Applicant</div>
              <div className="text-sm text-secondary">
                {r.applicantName ?? '—'}
                {r.applicantRole ? ` · ${r.applicantRole}` : ''}
              </div>
              <div className="text-sm text-secondary">
                {r.applicantEmail ?? '—'}
                {r.applicantPhone ? ` · ${r.applicantPhone}` : ''}
              </div>
            </div>

            {r.kind === 'company' ? (
              <div>
                <div className="text-sm font-medium">Company</div>
                <dl className="dl">
                  {r.industry && <><dt>Industry</dt><dd>{r.industry}</dd></>}
                  {r.companySize && <><dt>Size</dt><dd>{r.companySize}{r.employeeCount ? ` · ${r.employeeCount.toLocaleString('en-IN')} employees` : ''}</dd></>}
                  {r.foundedYear && <><dt>Founded</dt><dd>{r.foundedYear}</dd></>}
                  {(r.hqCity || r.hqState) && <><dt>HQ</dt><dd>{[r.hqCity, r.hqState].filter(Boolean).join(', ')}</dd></>}
                  {r.gstin && <><dt>GSTIN</dt><dd><code>{r.gstin}</code></dd></>}
                  {r.pan && <><dt>PAN</dt><dd><code>{r.pan}</code></dd></>}
                  {r.companyWebsite && <><dt>Website</dt><dd><a href={r.companyWebsite} target="_blank" rel="noreferrer" className="link">{r.companyWebsite}</a></dd></>}
                </dl>
                {r.hiringTargets && (
                  <div className="text-sm mt-2">
                    <b>Hiring targets:</b> {r.hiringTargets.yearlyHires ?? '?'} hires/year across {r.hiringTargets.campusesTargeted ?? '?'} campuses.
                    {r.hiringTargets.rolesTypical?.length ? ` Roles: ${r.hiringTargets.rolesTypical.join(', ')}.` : ''}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="text-sm font-medium">Institution</div>
                <dl className="dl">
                  {r.websiteUrl && <><dt>Website</dt><dd><a href={r.websiteUrl} target="_blank" rel="noreferrer" className="link">{r.websiteUrl}</a></dd></>}
                  {r.aisheColleges && r.aisheColleges.length > 0 && <><dt>AISHE matches</dt><dd>{r.aisheColleges.length}</dd></>}
                </dl>
                {r.additionalDetails && (
                  <div className="text-sm text-secondary mt-1">{r.additionalDetails}</div>
                )}
              </div>
            )}

            {r.verificationDocuments && r.verificationDocuments.length > 0 && (
              <div>
                <div className="text-sm font-medium">Verification documents</div>
                <ul className="text-sm mt-1 grid gap-1">
                  {r.verificationDocuments.map((d, i) => (
                    <li key={i}>
                      <span className="badge">{d.kind}</span>{' '}
                      {d.url.startsWith('stub://') ? (
                        <span className="text-secondary">{d.url} (dev stub)</span>
                      ) : (
                        <a href={d.url} target="_blank" rel="noreferrer" className="link">{d.url}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-2 mt-2 flex-wrap">
              <button
                className="btn btn-primary btn-sm"
                disabled={approve.isPending || r.status === 'approved'}
                onClick={() => approve.mutate(
                  { id: r._id, kind: r.kind, plan: r.kind === 'institution' ? 'college' : undefined },
                  {
                    onSuccess: () => toast.show({ message: `Provisioned ${orgName(r)}. Admin invite sent to ${r.applicantEmail ?? 'applicant'}.` }),
                    onError: (e: unknown) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Approve failed' }),
                  },
                )}
              >
                {approve.isPending ? 'Provisioning…' : 'Approve + provision'}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled
                title="Server-side status flow TBD"
              >
                Request more info
              </button>
              <button
                className="btn btn-danger btn-sm ml-auto"
                disabled={reject.isPending || r.status === 'rejected'}
                onClick={() => {
                  const reason = window.prompt('Reason for rejection (required, kept in audit log):');
                  if (!reason || reason.trim().length < 5) return;
                  reject.mutate(
                    { id: r._id, kind: r.kind, reason },
                    {
                      onSuccess: () => toast.show({ message: `Rejected ${orgName(r)}. Applicant notified.` }),
                      onError: (e: unknown) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Reject failed' }),
                    },
                  );
                }}
              >
                {reject.isPending ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        ),
      })}
      empty={{ title: 'Queue empty', hint: `No ${statusFilter} applications waiting review.` }}
      loading={query.isLoading}
      error={query.error}
    />
  );
}

function humanAge(d: Date): string {
  const ms = Date.now() - d.getTime();
  const h = Math.round(ms / 3600000);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}
